import { useEffect, useState, type ChangeEvent } from "react";
import type { MomentCarouselItem, MomentCommerceIntro, MomentCoupon, MomentMessageType } from "../../data/momentOptions";
import { SectionBox, SelectBox, ToggleSwitch, inputCls, inputSmCls } from "../shared";
import { MOMENT_IMAGE_ALLOWED_SIZES } from "../../utils/imageValidator";
import ImagePickerArea from "./ImagePickerArea";

type Props = {
  messageType: MomentMessageType;
  carouselItems: MomentCarouselItem[];
  commerceIntroEnabled: boolean;
  commerceIntro: MomentCommerceIntro;
  onCarouselItemChange: (
    id: string,
    key:
      | "messageTitle" | "description" | "pcLandingUrl" | "mobileLandingUrl"
      | "buttonTitle" | "buttonPcLandingUrl" | "buttonMobileLandingUrl"
      | "secondaryButtonTitle" | "secondaryButtonPcLandingUrl" | "secondaryButtonMobileLandingUrl"
      | "priceAmount" | "discountedPriceAmount",
    value: string
  ) => void;
  onCarouselItemImageChange: (id: string, event: ChangeEvent<HTMLInputElement>) => void;
  onCommerceIntroImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onCommerceIntroEnabledChange: (value: boolean) => void;
  onCommerceIntroChange: (
    key: "messageTitle" | "description" | "pcLandingUrl" | "mobileLandingUrl",
    value: string
  ) => void;
  onAddCarouselItem: () => void;
  onRemoveCarouselItem: (id: string) => void;
  onCarouselItemShareFlagChange: (id: string, value: boolean) => void;
  onCarouselItemSecondaryButtonEnabledChange: (id: string, value: boolean) => void;
  onCarouselItemCouponEnabledChange: (id: string, value: boolean) => void;
  onCarouselItemCouponChange: (id: string, key: keyof MomentCoupon, value: string) => void;
  onCarouselItemLibrarySelect?: (id: string, imgUrl: string) => void;
  onCommerceIntroLibrarySelect?: (imgUrl: string) => void;
};

export default function CarouselFields({
  messageType, carouselItems,
  commerceIntroEnabled, commerceIntro,
  onCarouselItemChange, onCarouselItemImageChange,
  onCommerceIntroImageChange, onCommerceIntroEnabledChange, onCommerceIntroChange,
  onAddCarouselItem, onRemoveCarouselItem,
  onCarouselItemShareFlagChange,
  onCarouselItemSecondaryButtonEnabledChange,
  onCarouselItemCouponEnabledChange, onCarouselItemCouponChange,
  onCarouselItemLibrarySelect, onCommerceIntroLibrarySelect,
}: Props) {
  const [couponPcEnabled, setCouponPcEnabled] = useState<Record<string, boolean>>({});
  const [landingPcEnabled, setLandingPcEnabled] = useState<Record<string, boolean>>({});
  const [secondaryButtonPcEnabled, setSecondaryButtonPcEnabled] = useState<Record<string, boolean>>({});
  const isFeed = messageType === "CAROUSEL_FEED_MESSAGE";
  const titleMaxLength = isFeed ? 20 : 25;
  const descriptionMaxLength = 50;
  const descriptionMaxNewlines = isFeed ? 2 : 2;

  useEffect(() => {
    setCouponPcEnabled(
      Object.fromEntries(
        carouselItems.map((item) => [item.id, item.coupon.pcLandingUrl.trim().length > 0])
      )
    );
    setLandingPcEnabled(
      Object.fromEntries(
        carouselItems.map((item) => [
          item.id,
          isFeed
            ? item.buttonPcLandingUrl.trim().length > 0
            : item.pcLandingUrl.trim().length > 0,
        ])
      )
    );
    if (!isFeed) {
      setLandingPcEnabled((prev) => ({
        ...prev,
        "commerce-intro": commerceIntro.pcLandingUrl.trim().length > 0,
      }));
    }
    setSecondaryButtonPcEnabled(
      Object.fromEntries(
        carouselItems.map((item) => [item.id, item.secondaryButtonPcLandingUrl.trim().length > 0])
      )
    );
  }, [carouselItems, isFeed, commerceIntro.pcLandingUrl]);


  return (
    <>
      {!isFeed && (
        <SectionBox label="인트로 카드" enabled={commerceIntroEnabled} onToggle={onCommerceIntroEnabledChange}>
          <div className="tw-space-y-3">
            <div>
              <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1.5">
                <span className="tw-text-red-500 tw-mr-1">*</span>
                홍보 이미지
              </label>
              <ImagePickerArea
                imageName={commerceIntro.imageName}
                sizeHint="800×400 / 800×800 / 800×600px"
                allowedSizes={MOMENT_IMAGE_ALLOWED_SIZES.CAROUSEL_COMMERCE}
                onFileChange={onCommerceIntroImageChange}
                onLibrarySelect={onCommerceIntroLibrarySelect}
                compact
              />
            </div>
            <div className="tw-space-y-1.5">
              <label className="tw-block tw-text-xs tw-text-gray-500">
                <span className="tw-text-red-500 tw-mr-1">*</span>
                카드 타이틀
              </label>
              <input
                value={commerceIntro.messageTitle}
                onChange={(e) => onCommerceIntroChange("messageTitle", e.target.value.replace(/\n/g, "").slice(0, 20))}
                placeholder="인트로 타이틀"
                className={inputCls}
              />
              <div className="tw-flex tw-justify-end tw-text-xs tw-text-gray-400">
                <span>{commerceIntro.messageTitle.length} / 20</span>
              </div>
            </div>
            <div className="tw-space-y-1.5">
              <label className="tw-block tw-text-xs tw-text-gray-500">
                <span className="tw-text-red-500 tw-mr-1">*</span>
                홍보 문구
              </label>
              <textarea
                rows={2}
                value={commerceIntro.description}
                onChange={(e) => onCommerceIntroChange("description", e.target.value.replace(/\n/g, "").slice(0, 50))}
                placeholder="인트로 홍보 문구"
                className={`${inputCls} tw-resize-none`}
              />
              <div className="tw-flex tw-justify-end tw-gap-4 tw-text-xs tw-text-gray-400">
                <span>개행 0 / 0</span>
                <span>{commerceIntro.description.length} / 50</span>
              </div>
            </div>
            <div className="tw-space-y-1.5">
              <label className="tw-block tw-text-xs tw-text-gray-500">
                <span className="tw-text-red-500 tw-mr-1">*</span>
                모바일 랜딩 URL
              </label>
              <input
                value={commerceIntro.mobileLandingUrl}
                onChange={(e) => onCommerceIntroChange("mobileLandingUrl", e.target.value)}
                placeholder="모바일 랜딩 URL"
                className={inputCls}
              />
            </div>
            <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer tw-w-fit">
              <input
                type="checkbox"
                checked={landingPcEnabled["commerce-intro"] ?? false}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setLandingPcEnabled((prev) => ({ ...prev, "commerce-intro": checked }));
                  if (!checked) onCommerceIntroChange("pcLandingUrl", "");
                }}
                className="tw-w-3.5 tw-h-3.5"
              />
              PC 링크
            </label>
            {(landingPcEnabled["commerce-intro"] ?? false) && (
              <div className="tw-space-y-1.5">
                <label className="tw-block tw-text-xs tw-text-gray-500">PC 랜딩 URL</label>
                <input
                  value={commerceIntro.pcLandingUrl}
                  onChange={(e) => onCommerceIntroChange("pcLandingUrl", e.target.value)}
                  placeholder="PC 랜딩 URL"
                  className={inputCls}
                />
              </div>
            )}
          </div>
        </SectionBox>
      )}
      <SectionBox label="소재" required>
        <div className="tw-space-y-3">
        <div className="tw-flex tw-justify-between tw-items-center">
          <span className="tw-text-xs tw-text-gray-500">
            {!isFeed && commerceIntroEnabled ? "1~6장" : "2~6장"}
          </span>
          <button
            type="button"
            onClick={onAddCarouselItem}
            disabled={carouselItems.length >= 6}
            className="tw-text-xs tw-bg-blue-50 tw-text-blue-600 tw-px-3 tw-py-1 tw-rounded-lg tw-border tw-border-blue-200 disabled:tw-opacity-40"
          >
            + 카드 추가
          </button>
        </div>
        {carouselItems.map((item, index) => (
          <div key={item.id} className="tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden">
            {/* 카드 헤더 */}
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-b tw-border-gray-200">
              <span className="tw-text-sm tw-font-semibold tw-text-gray-800">
                카드 {index + 1}
                {!isFeed && index === 0 && (
                  <span className="tw-text-xs tw-text-gray-400 tw-font-normal tw-ml-1.5">(인트로 카드 기준)</span>
                )}
              </span>
                <button
                  type="button"
                  onClick={() => onRemoveCarouselItem(item.id)}
                  disabled={carouselItems.length <= (!isFeed && commerceIntroEnabled ? 1 : 2)}
                  className="tw-text-xs tw-bg-red-50 tw-text-red-400 tw-px-3 tw-py-1 tw-rounded-lg tw-border tw-border-red-200 disabled:tw-opacity-30"
                >
                삭제
              </button>
            </div>

            {/* 카드 기본 정보 */}
            <div className="tw-p-4 tw-space-y-3 tw-border-b tw-border-gray-100">
              <div>
                <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1.5">
                  <span className="tw-text-red-500 tw-mr-1">*</span>
                  홍보 이미지
                </label>
                <ImagePickerArea
                  imageName={item.imageName}
                  sizeHint={isFeed ? "800×400 / 800×600px" : "800×400 / 800×800 / 800×600px"}
                  allowedSizes={isFeed ? MOMENT_IMAGE_ALLOWED_SIZES.CAROUSEL_FEED : MOMENT_IMAGE_ALLOWED_SIZES.CAROUSEL_COMMERCE}
                  onFileChange={(e) => onCarouselItemImageChange(item.id, e)}
                  onLibrarySelect={onCarouselItemLibrarySelect ? (url) => onCarouselItemLibrarySelect(item.id, url) : undefined}
                  compact
                />
              </div>
              <div className="tw-space-y-1.5">
                <label className="tw-block tw-text-xs tw-text-gray-500">
                  <span className="tw-text-red-500 tw-mr-1">*</span>
                  카드 타이틀
                </label>
                <input
                  value={item.messageTitle}
                  onChange={(e) => onCarouselItemChange(item.id, "messageTitle", e.target.value.slice(0, titleMaxLength))}
                  placeholder="카드 타이틀"
                  className={inputCls}
                />
                <div className="tw-flex tw-justify-end tw-text-xs tw-text-gray-400">
                  <span>{item.messageTitle.length} / {titleMaxLength}</span>
                </div>
              </div>
              <div className="tw-space-y-1.5">
                <label className="tw-block tw-text-xs tw-text-gray-500">
                  <span className="tw-text-red-500 tw-mr-1">*</span>
                  홍보 문구
                </label>
                <textarea
                  rows={2}
                  value={item.description}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    if ((nextValue.match(/\n/g) || []).length > descriptionMaxNewlines) return;
                    onCarouselItemChange(item.id, "description", nextValue.slice(0, descriptionMaxLength));
                  }}
                  placeholder="카드 설명"
                  className={`${inputCls} tw-resize-none`}
                />
                <div className="tw-flex tw-justify-end tw-gap-4 tw-text-xs tw-text-gray-400">
                  <span>개행 {(item.description.match(/\n/g) || []).length} / {descriptionMaxNewlines}</span>
                  <span>{item.description.length} / {descriptionMaxLength}</span>
                </div>
              </div>
              {!isFeed && (
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                  <div className="tw-space-y-1.5">
                    <label className="tw-block tw-text-xs tw-text-gray-500">
                      <span className="tw-text-red-500 tw-mr-1">*</span>
                      가격 정보
                    </label>
                    <input
                      value={item.priceAmount}
                      onChange={(e) => onCarouselItemChange(item.id, "priceAmount", e.target.value.replace(/\D/g, "").slice(0, 8))}
                      placeholder="가격 정보"
                      className={inputSmCls}
                    />
                  </div>
                  <div className="tw-space-y-1.5">
                    <label className="tw-block tw-text-xs tw-text-gray-500">
                      할인 가격 정보
                    </label>
                    <input
                      value={item.discountedPriceAmount}
                      onChange={(e) => onCarouselItemChange(item.id, "discountedPriceAmount", e.target.value.replace(/\D/g, "").slice(0, 8))}
                      placeholder="할인 가격 정보"
                      className={inputSmCls}
                    />
                    <p className="tw-text-[11px] tw-leading-4 tw-text-gray-400">
                      가격 정보값 미만의 1% 이상 차이나는 값 입력
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* 버튼1 */}
            <div className="tw-border-b tw-border-gray-100">
              <div className="tw-px-4 tw-py-2.5 tw-bg-gray-50/60">
                <span className="tw-text-xs tw-font-semibold tw-text-gray-600">
                  {isFeed ? "버튼1" : "구매 버튼"}
                </span>
              </div>
              <div className="tw-px-4 tw-py-3 tw-space-y-2">
                {isFeed ? (
                  <div className="tw-space-y-1.5">
                    <label className="tw-block tw-text-xs tw-text-gray-500">
                      <span className="tw-text-red-500 tw-mr-1">*</span>
                      버튼명
                    </label>
                    <input
                      value={item.buttonTitle}
                      onChange={(e) => onCarouselItemChange(item.id, "buttonTitle", e.target.value.slice(0, 8))}
                      placeholder="버튼명 (최대 8자)"
                      maxLength={8}
                      className={inputCls}
                    />
                    <div className="tw-flex tw-justify-end tw-text-xs tw-text-gray-400">
                      <span>{item.buttonTitle.length} / 8</span>
                    </div>
                  </div>
                ) : null}
                {isFeed && (
                  <>
                    <div className="tw-space-y-1.5">
                      <label className="tw-block tw-text-xs tw-text-gray-500">
                        <span className="tw-text-red-500 tw-mr-1">*</span>
                        모바일 랜딩 URL
                      </label>
                      <input
                        value={item.buttonMobileLandingUrl}
                        onChange={(e) => onCarouselItemChange(item.id, "buttonMobileLandingUrl", e.target.value)}
                        placeholder="모바일 랜딩 URL"
                        className={inputCls}
                      />
                    </div>
                    <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer tw-w-fit">
                      <input
                        type="checkbox"
                        checked={landingPcEnabled[item.id] ?? false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setLandingPcEnabled((prev) => ({ ...prev, [item.id]: checked }));
                          if (!checked) onCarouselItemChange(item.id, "buttonPcLandingUrl", "");
                        }}
                        className="tw-w-3.5 tw-h-3.5"
                      />
                      PC 링크
                    </label>
                    {landingPcEnabled[item.id] && (
                      <div className="tw-space-y-1.5">
                        <label className="tw-block tw-text-xs tw-text-gray-500">PC 랜딩 URL</label>
                        <input
                          value={item.buttonPcLandingUrl}
                          onChange={(e) => onCarouselItemChange(item.id, "buttonPcLandingUrl", e.target.value)}
                          placeholder="PC 랜딩 URL"
                          className={inputCls}
                        />
                      </div>
                    )}
                    <p className="tw-text-xs tw-text-gray-500">
                      카드 이미지와 홍보 문구는 버튼1 랜딩 URL로 연결됩니다.
                    </p>
                  </>
                )}
                {!isFeed && (
                  <>
                    <label className="tw-block tw-text-xs tw-text-gray-500">
                      <span className="tw-text-red-500 tw-mr-1">*</span>
                      모바일 랜딩 URL
                    </label>
                    <input
                      value={item.mobileLandingUrl}
                      onChange={(e) => onCarouselItemChange(item.id, "mobileLandingUrl", e.target.value)}
                      placeholder="모바일 랜딩 URL"
                      className={inputCls}
                    />
                    <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer tw-w-fit">
                      <input
                        type="checkbox"
                        checked={landingPcEnabled[item.id] ?? false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setLandingPcEnabled((prev) => ({ ...prev, [item.id]: checked }));
                          if (!checked) onCarouselItemChange(item.id, "pcLandingUrl", "");
                        }}
                        className="tw-w-3.5 tw-h-3.5"
                      />
                      PC 링크
                    </label>
                    {landingPcEnabled[item.id] && (
                      <input
                        value={item.pcLandingUrl}
                        onChange={(e) => onCarouselItemChange(item.id, "pcLandingUrl", e.target.value)}
                        placeholder="PC 랜딩 URL"
                        className={inputCls}
                      />
                    )}
                    <p className="tw-text-xs tw-text-gray-500">
                      카드 이미지와 구매 버튼은 동일한 랜딩 URL로 연결됩니다.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* 버튼2 (FEED 전용) */}
            {isFeed && (
              <>
                <div className="tw-border-b tw-border-gray-100">
                  <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2.5 tw-bg-gray-50/60">
                    <span className="tw-text-xs tw-font-semibold tw-text-gray-600">
                      버튼2 <span className="tw-text-xs tw-text-gray-400 tw-font-normal">(선택)</span>
                    </span>
                    <ToggleSwitch
                      checked={item.secondaryButtonEnabled}
                      onChange={(value) => {
                        if (value && item.shareFlag) {
                          const confirmed = window.confirm("공유하기가 해제되고 버튼2 사용으로 변경됩니다. 그래도 사용하시겠습니까?");
                          if (!confirmed) return;
                        }
                        onCarouselItemSecondaryButtonEnabledChange(item.id, value);
                      }}
                    />
                  </div>
                    {item.secondaryButtonEnabled && (
                      <div className="tw-px-4 tw-py-3 tw-space-y-2">
                        <div className="tw-space-y-1.5">
                        <label className="tw-block tw-text-xs tw-text-gray-500">
                          <span className="tw-text-red-500 tw-mr-1">*</span>
                          버튼명
                        </label>
                        <input
                          value={item.secondaryButtonTitle}
                          onChange={(e) => onCarouselItemChange(item.id, "secondaryButtonTitle", e.target.value.slice(0, 8))}
                          placeholder="버튼명 (최대 8자)"
                          maxLength={8}
                          className={inputCls}
                        />
                        <div className="tw-flex tw-justify-end tw-text-xs tw-text-gray-400">
                          <span>{item.secondaryButtonTitle.length} / 8</span>
                        </div>
                        </div>
                        <div className="tw-space-y-1.5">
                          <label className="tw-block tw-text-xs tw-text-gray-500">
                            <span className="tw-text-red-500 tw-mr-1">*</span>
                            모바일 랜딩 URL
                          </label>
                          <input
                            value={item.secondaryButtonMobileLandingUrl}
                            onChange={(e) => onCarouselItemChange(item.id, "secondaryButtonMobileLandingUrl", e.target.value)}
                            placeholder="모바일 랜딩 URL"
                            className={inputCls}
                          />
                        </div>
                        <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer tw-w-fit">
                          <input
                            type="checkbox"
                            checked={secondaryButtonPcEnabled[item.id] ?? false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSecondaryButtonPcEnabled((prev) => ({ ...prev, [item.id]: checked }));
                              if (!checked) onCarouselItemChange(item.id, "secondaryButtonPcLandingUrl", "");
                            }}
                            className="tw-w-3.5 tw-h-3.5"
                          />
                          PC 링크
                        </label>
                        {secondaryButtonPcEnabled[item.id] && (
                          <div className="tw-space-y-1.5">
                            <label className="tw-block tw-text-xs tw-text-gray-500">PC 랜딩 URL</label>
                            <input
                              value={item.secondaryButtonPcLandingUrl}
                              onChange={(e) => onCarouselItemChange(item.id, "secondaryButtonPcLandingUrl", e.target.value)}
                              placeholder="PC 랜딩 URL"
                              className={inputCls}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                <div className="tw-border-b tw-border-gray-100">
                  <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2.5 tw-bg-gray-50/60">
                    <span className="tw-text-xs tw-font-semibold tw-text-gray-600">
                      공유하기 <span className="tw-text-xs tw-text-gray-400 tw-font-normal">(선택)</span>
                    </span>
                    <ToggleSwitch
                      checked={item.shareFlag}
                      onChange={(value) => {
                        if (!value) {
                          onCarouselItemShareFlagChange(item.id, false);
                          return;
                        }

                        if (item.secondaryButtonEnabled) {
                          const confirmed = window.confirm("버튼2가 해제되고 공유하기 사용으로 변경됩니다. 그래도 사용하시겠습니까?");
                          if (!confirmed) return;
                        }

                        onCarouselItemShareFlagChange(item.id, true);
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            {/* 쿠폰 (FEED 전용) */}
            {isFeed && (
              <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2.5 tw-bg-gray-50/60">
                  <span className="tw-text-xs tw-font-semibold tw-text-gray-600">
                    쿠폰 <span className="tw-text-xs tw-text-gray-400 tw-font-normal">(선택)</span>
                  </span>
                  <ToggleSwitch checked={item.couponEnabled} onChange={(v) => onCarouselItemCouponEnabledChange(item.id, v)} />
                </div>
                {item.couponEnabled && (
                  <div className="tw-px-4 tw-py-3 tw-border-t tw-border-gray-100 tw-space-y-2">
                    <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                      <div className="tw-space-y-1.5">
                        <input
                          value={item.coupon.couponBookTitle}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const next =
                              item.coupon.couponBookTitleType === "DISCOUNT_PRICE"
                                ? raw.replace(/\D/g, "").slice(0, 8)
                                : item.coupon.couponBookTitleType === "DISCOUNT_RATE"
                                  ? raw.replace(/\D/g, "").slice(0, 2)
                                  : raw.slice(0, 7);
                            onCarouselItemCouponChange(item.id, "couponBookTitle", next);
                          }}
                          placeholder="쿠폰 타이틀"
                          disabled={item.coupon.couponBookTitleType === "FREE_SHIPPING"}
                          className={inputSmCls}
                        />
                        {item.coupon.couponBookTitleType !== "FREE_SHIPPING" && (
                          <div className="tw-flex tw-justify-end tw-text-xs tw-text-gray-400">
                            <span>
                              {item.coupon.couponBookTitle.length} / {
                                item.coupon.couponBookTitleType === "DISCOUNT_PRICE"
                                  ? 8
                                  : item.coupon.couponBookTitleType === "DISCOUNT_RATE"
                                    ? 2
                                    : 7
                              }
                            </span>
                          </div>
                        )}
                      </div>
                      <SelectBox
                        value={item.coupon.couponBookTitleType}
                        onChange={(val) => {
                          onCarouselItemCouponChange(item.id, "couponBookTitleType", val);
                          onCarouselItemCouponChange(item.id, "couponBookTitle", "");
                        }}
                        options={[
                          { value: "DISCOUNT_PRICE", label: "할인금액" },
                          { value: "DISCOUNT_RATE", label: "할인율" },
                          { value: "FREE_SHIPPING", label: "배송비 할인" },
                          { value: "FREE_GIFT", label: "무료 증정" },
                          { value: "UPGRADE", label: "업그레이드" },
                        ]}
                        className="tw-w-full"
                      />
                    </div>
                    <input
                      value={item.coupon.title}
                      onChange={(e) => onCarouselItemCouponChange(item.id, "title", e.target.value.slice(0, 12))}
                      placeholder="쿠폰 상세 설명 (최대 12자)"
                      className={inputCls}
                    />
                    <div className="tw-flex tw-justify-end tw-text-xs tw-text-gray-400">
                      <span>{item.coupon.title.length} / 12</span>
                    </div>
                    <input
                      value={item.coupon.mobileLandingUrl}
                      onChange={(e) => onCarouselItemCouponChange(item.id, "mobileLandingUrl", e.target.value)}
                      placeholder="쿠폰 모바일 랜딩 URL"
                      className={inputCls}
                    />
                    <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer tw-w-fit">
                      <input
                        type="checkbox"
                        checked={couponPcEnabled[item.id] ?? false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setCouponPcEnabled((prev) => ({ ...prev, [item.id]: checked }));
                          if (!checked) {
                            onCarouselItemCouponChange(item.id, "pcLandingUrl", "");
                          }
                        }}
                        className="tw-w-3.5 tw-h-3.5"
                      />
                      PC 링크
                    </label>
                    {couponPcEnabled[item.id] && (
                      <input
                        value={item.coupon.pcLandingUrl}
                        onChange={(e) => onCarouselItemCouponChange(item.id, "pcLandingUrl", e.target.value)}
                        placeholder="쿠폰 PC 랜딩 URL"
                        className={inputCls}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        </div>
      </SectionBox>

    </>
  );
}
